/**
 * DataTable 컴포넌트
 * - tanstack-table 기반 테이블 컴포넌트
 * - 페이지네이션, 정렬, 컬럼 리사이징, 행 선택 지원
 */

import {
  type ColumnDef,
  type SortingState,
  type RowSelectionState,
  type ColumnResizeMode,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import * as React from "react";

import { PAGE_SIZE_OPTIONS } from "@/common/constant";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination } from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { UpDown } from "@/components/icons/UpDown";
import { ChevronDown, FileIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import type { OrderDirection } from "@/common/store/tableStore";
import { formatCount } from "@/common/utils/format";

/**
 * 행의 고유 id. TanStack 기본값은 행 index 라서, 목록이 재정렬되면 같은 위치의 React
 * 엘리먼트가 다른 엔티티의 데이터를 물려받는다 — 열어 둔 상세 Drawer 가 (내부 open 상태는
 * 그대로인 채) 다른 항목으로 바뀌는 원인이다. 엔티티 키가 있으면 그것을 쓴다.
 */
const resolveRowId = <TData,>(row: TData, index: number): string => {
  const { uuid, id } = (row ?? {}) as { uuid?: unknown; id?: unknown };
  const key = uuid ?? id;
  return typeof key === "string" || typeof key === "number"
    ? String(key)
    : String(index);
};

// TanStack 과 같게 id 를 우선한다 — 어긋나면 id 와 accessorKey 가 다른 열의 너비가 적용되지 않는다
const columnKey = <TData, TValue>(col: ColumnDef<TData, TValue>): string =>
  col.id ?? (col as { accessorKey?: string }).accessorKey ?? "";

/** 좌우 스크롤과 무관하게 테이블 우측에 항상 고정되는 열 (예: 관리 액션 열) */
const isStickyRightColumn = <TData, TValue>(
  columnDef: ColumnDef<TData, TValue>,
): boolean =>
  (columnDef.meta as { sticky?: "right" } | undefined)?.sticky === "right";

/**
 * 체크박스 열처럼 셀 패딩 없이 내용을 가운데 정렬해야 하는 열.
 * `[&:has([role=checkbox])]:pr-0` (table.tsx) 가 오른쪽 패딩만 지워 비대칭이라 필요
 */
const hasCenteredContent = <TData, TValue>(
  columnDef: ColumnDef<TData, TValue>,
): boolean => (columnDef.meta as { align?: "center" } | undefined)?.align === "center";

/**
 * 그룹 컬럼(하위 columns 를 가진 정의)을 펼쳐 잎 정의만 남긴다.
 * 폭 계산과 리사이즈는 잎 컬럼 단위다 — 그룹 헤더는 자기 폭을 갖지 않는다
 */
const flattenLeafColumns = <TData, TValue>(
  columns: ColumnDef<TData, TValue>[],
): ColumnDef<TData, TValue>[] =>
  columns.flatMap((col) =>
    "columns" in col && col.columns
      ? flattenLeafColumns(col.columns as ColumnDef<TData, TValue>[])
      : [col],
  );

/** 행 클릭이 켜진 `<tr>` 마커. 바깥 클릭으로 닫히는 드로어가 행 클릭을 제외할 때 쓴다 */
const ROW_CLICKABLE_ATTR = "data-row-clickable";

/** 셀 안의 링크·버튼·입력은 행 클릭으로 보지 않는다 */
const ROW_CLICK_IGNORE_SELECTOR =
  "a, button, input, select, textarea, [role=button], [role=checkbox], [role=menuitem]";

/** 셀의 링크·버튼 클릭과 텍스트 드래그 선택은 행 클릭이 아니다 */
function isRowClick(e: React.MouseEvent<HTMLTableRowElement>): boolean {
  if ((e.target as Element).closest(ROW_CLICK_IGNORE_SELECTOR)) return false;
  const selection = window.getSelection();
  return !(
    selection &&
    selection.toString() &&
    e.currentTarget.contains(selection.anchorNode)
  );
}

// TODO : 줄이면 아무 것도 없는 빈열이 나머지 공간을 차지하도록 해야함 26.01.29
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  enableRowSelection?: boolean;
  /** 행 선택 상태를 외부에서 제어할 때 사용 (성공 후 초기화 등). 없으면 내부 상태로 관리 */
  rowSelection?: RowSelectionState;
  enableColumnResizing?: boolean;
  columnResizeMode?: ColumnResizeMode;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  onColumnSizingChange?: (sizing: ColumnSizingState) => void;
  enablePagination?: boolean;

  /** API 요청용 offset (0부터 시작) */
  offset?: number;
  /** API 요청용 limit (페이지당 항목 수) */
  limit?: number;
  /** 페이지네이션 변경 콜백 */
  onPaginationChange?: (offset: number, limit: number) => void;
  totalItems?: number;
  pageSizeOptions?: number[];
  showToolbar?: boolean;

  /** order */
  toggleOrder?: (columnId: string | null) => void;
  orderDirection?: OrderDirection;
  orderBy?: string | null;

  /** 로딩 상태 - true일 때 스켈레톤 표시 */
  isLoading?: boolean;
  /** 스켈레톤 row 개수 (기본값: 15) */
  skeletonRowCount?: number;

  /**
   * 높이가 정해진 컨테이너(모달 등)를 채우는 테이블.
   * 빈 상태 블록이 남는 높이에 맞춰 줄어든다 — 페이지용 고정 높이를 그대로 쓰면 넘친다
   */
  fillHeight?: boolean;

  /** 열 표시/순서 편집 (기본값: false) */
  enableColumnSettings?: boolean;
  /** 열 표시/순서 편집용 ReactNode (enableColumnSettings가 true일 때 toolbar 오른쪽에 렌더) */
  columnSettingsSlot?: React.ReactNode;
  /** 툴바 좌측 라벨을 대체한다 (예: 선택된 행이 있을 때 "선택한 충전기 N기"). 없으면 "조회 항목 N건" */
  toolbarLeftLabel?: React.ReactNode;
  /** 행 선택 시의 일괄 액션 ReactNode (열 표시/순서 편집 버튼 왼쪽에 렌더) */
  bulkActionsSlot?: React.ReactNode;

  /** 행 클릭 (상세 드로어 열기 등). 셀 안의 링크·버튼 클릭은 제외된다 */
  onRowClick?: (row: TData) => void;
  /** 선택 색으로 하이라이트할 행의 id (`uuid ?? id`) */
  activeRowId?: string | null;
}

function DataTable<TData, TValue>({
  columns,
  data,
  enableRowSelection = false,
  rowSelection: controlledRowSelection,
  enableColumnResizing = false,
  columnResizeMode = "onEnd",
  onRowSelectionChange: onRowSelectionChangeProp,
  onColumnSizingChange,
  enablePagination = false,
  offset = 0,
  limit = 15,
  onPaginationChange,
  totalItems,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  showToolbar = false,
  toggleOrder,
  orderDirection,
  orderBy,
  isLoading = false,
  skeletonRowCount = 15,
  fillHeight = false,
  enableColumnSettings = false,
  columnSettingsSlot,
  toolbarLeftLabel,
  bulkActionsSlot,
  onRowClick,
  activeRowId = null,
}: DataTableProps<TData, TValue>) {
  // offset/limit을 tanstack-table의 pageIndex/pageSize로 변환
  const pageIndex = Math.floor(offset / limit);
  const pageSize = limit;

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [internalRowSelection, setInternalRowSelection] =
    React.useState<RowSelectionState>({});
  // 선택 상태를 외부(rowSelection prop)에서 줄 수도, 내부에서만 들고 있을 수도 있다 —
  // 성공 후 초기화처럼 외부에서 리셋해야 하는 화면만 controlled 로 쓴다
  const rowSelection = controlledRowSelection ?? internalRowSelection;
  // 한 이벤트 안에서 toggleSelected 를 연달아 호출하면 매번 렌더 시점의 rowSelection
  // 클로저를 기준으로 계산돼 뒤 호출이 앞 호출을 덮어쓴다 — ref로 즉시 반영해 누적시킨다
  const rowSelectionRef = React.useRef(rowSelection);
  rowSelectionRef.current = rowSelection;
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>({});
  const tableRef = React.useRef<HTMLTableElement>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);
  const [resizingColumnId, setResizingColumnId] = React.useState<string | null>(
    null,
  );
  const resizeStartX = React.useRef<number>(0);
  const resizeStartWidth = React.useRef<number>(0);
  // 컨테이너 폭. 렌더 중 ref 를 읽으면 리사이즈에 반응하지 못해 state 로 둔다
  const [containerWidth, setContainerWidth] = React.useState(0);
  // 사용자가 직접 드래그해 폭을 정한 컬럼 — 자동 분배 대상에서 제외한다
  const manuallyResizedRef = React.useRef<Set<string>>(new Set());
  // 고정 열은 리사이즈·유동폭 분배 대상이 아니다 — 별도 테이블에 자기 폭 그대로 그려진다
  const leafColumns = React.useMemo(
    () => flattenLeafColumns(columns).filter((col) => !isStickyRightColumn(col)),
    [columns],
  );

  const table = useReactTable({
    data,
    columns,
    getRowId: resolveRowId,
    defaultColumn: {
      enableResizing: true,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableRowSelection,
    onRowSelectionChange: (updater) => {
      const newSelection =
        typeof updater === "function"
          ? updater(rowSelectionRef.current)
          : updater;
      rowSelectionRef.current = newSelection;
      if (controlledRowSelection === undefined) {
        setInternalRowSelection(newSelection);
      }
      onRowSelectionChangeProp?.(newSelection);
    },
    onColumnSizingChange: (updater) => {
      const newSizing =
        typeof updater === "function" ? updater(columnSizing) : updater;

      // 각 컬럼의 minSize 또는 size 이하로 줄어들지 않도록 제한
      const constrainedSizing: ColumnSizingState = {};
      for (const [columnId, newWidth] of Object.entries(newSizing)) {
        const columnDef = leafColumns.find(
          (col) => columnKey(col) === columnId,
        );
        if (columnDef) {
          const minAllowedSize = columnDef.minSize ?? columnDef.size ?? 0;
          constrainedSizing[columnId] = Math.max(newWidth, minAllowedSize);
        } else {
          constrainedSizing[columnId] = newWidth;
        }
      }

      setColumnSizing(constrainedSizing);
      onColumnSizingChange?.(constrainedSizing);
    },
    enableColumnResizing,
    columnResizeMode,
    ...(enablePagination && {
      // 서버 사이드 페이징 (totalItems가 있을 때)
      ...(totalItems !== undefined
        ? { manualPagination: true }
        : { getPaginationRowModel: getPaginationRowModel() }),
      onPaginationChange: (updater) => {
        const currentPagination = { pageIndex, pageSize };
        const newPagination =
          typeof updater === "function" ? updater(currentPagination) : updater;

        const newOffset = newPagination.pageIndex * newPagination.pageSize;
        const newLimit = newPagination.pageSize;
        onPaginationChange?.(newOffset, newLimit);
      },
    }),
    state: {
      sorting,
      rowSelection,
      columnSizing,
      ...(enablePagination && { pagination: { pageIndex, pageSize } }),
    },
  });

  // 컨테이너 폭을 관찰한다 — 화면이 넓어지면 유동 컬럼이 남는 공간을 흡수해야 해서
  // 최초 1회 측정으로는 부족하다. 빈 상태 UI 로 컨테이너가 재마운트되므로 콜백 ref 로 붙인다
  const attachContainer = React.useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    containerRef.current = node;
    if (!node) return;

    setContainerWidth(node.clientWidth);
    const observer = new ResizeObserver(([entry]) =>
      setContainerWidth(entry.target.clientWidth),
    );
    observer.observe(node);
    resizeObserverRef.current = observer;
  }, []);

  // 유동 컬럼(size 없이 minSize 만 있는 컬럼)에 남는 공간을 minSize 비율로 분배한다.
  // 고정 컬럼(size)은 절대 늘리지 않는다
  React.useLayoutEffect(() => {
    if (containerWidth === 0) return;

    const flexCols = leafColumns.filter(
      (col) =>
        col.size === undefined &&
        !manuallyResizedRef.current.has(columnKey(col)),
    );
    if (flexCols.length === 0) return;

    // 고정 컬럼 + 사용자가 직접 정한 컬럼은 이미 정해진 폭을 차지한다
    const reservedWidth = leafColumns.reduce((sum, col) => {
      const colId = columnKey(col);
      if (col.size !== undefined) return sum + col.size;
      if (manuallyResizedRef.current.has(colId)) {
        return sum + (columnSizing[colId] ?? col.minSize ?? 0);
      }
      return sum;
    }, 0);

    const flexMinSizeSum = flexCols.reduce(
      (sum, col) => sum + (col.minSize ?? 0),
      0,
    );
    const extraSpace = Math.max(
      0,
      containerWidth - reservedWidth - flexMinSizeSum,
    );

    const nextSizing: ColumnSizingState = {};
    flexCols.forEach((col) => {
      const colId = columnKey(col);
      const minSize = col.minSize ?? 0;
      const ratio =
        flexMinSizeSum > 0 ? minSize / flexMinSizeSum : 1 / flexCols.length;
      nextSizing[colId] = minSize + Math.floor(extraSpace * ratio);
    });

    setColumnSizing((prev) => {
      // 값이 같으면 새 객체를 만들지 않는다 — ResizeObserver 콜백이 매번 리렌더를 유발하지 않게
      const unchanged = Object.entries(nextSizing).every(
        ([id, width]) => prev[id] === width,
      );
      return unchanged ? prev : { ...prev, ...nextSizing };
    });
    // columnSizing 을 deps 에 넣으면 위 setColumnSizing 과 맞물려 루프가 된다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafColumns, containerWidth]);

  const headerGroupCount = table.getHeaderGroups().length;

  // 각 컬럼의 너비: columnSizing에 있으면 그 값, 없으면 size 또는 minSize 사용
  const getColumnWidth = (
    column: ReturnType<typeof table.getAllColumns>[number],
  ): number | undefined => {
    const colId = column.id;
    if (columnSizing[colId] !== undefined) {
      return columnSizing[colId];
    }
    // size가 있으면 고정 너비
    if (column.columnDef.size !== undefined) {
      return column.columnDef.size;
    }
    // 초기화 전 flex 컬럼은 minSize 반환 (있으면)
    if (column.columnDef.minSize !== undefined) {
      return column.columnDef.minSize;
    }
    return undefined;
  };

  // 관리 열처럼 항상 보이는 열은 별도 고정 테이블로 뺀다 — position:sticky만 쓰면
  // 스크롤 전에도 뷰포트 우측 끝에 붙어, 아직 스크롤 안 한 다른 열을 덮어버린다
  const leafTableColumns = table.getAllLeafColumns();
  const stickyColumns = leafTableColumns.filter((col) =>
    isStickyRightColumn(col.columnDef),
  );
  const scrollableColumns = leafTableColumns.filter(
    (col) => !isStickyRightColumn(col.columnDef),
  );
  const stickyTotalWidth = stickyColumns.reduce(
    (sum, col) => sum + (getColumnWidth(col) ?? 0),
    0,
  );
  // 가로 스크롤이 뜨면 스크롤 영역 아래에 트랙 높이(16px, global.css)만큼 여백이 생긴다.
  // 고정 열 쪽 높이를 맞추려고 flex 로 늘려 둔 자리라 흰 배경만 있으면 색이 떠 보인다
  const scrollableContentWidth = scrollableColumns.reduce(
    (sum, col) => sum + (getColumnWidth(col) ?? 0),
    0,
  );
  const hasHorizontalScroll =
    containerWidth > 0 && scrollableContentWidth > containerWidth;

  // 테이블 전체 너비: 컬럼 합이 컨테이너보다 크면 컬럼 합 사용, 아니면 100%
  const hasColumnSizing = Object.keys(columnSizing).length > 0;
  const columnsTotal = hasColumnSizing
    ? scrollableColumns.reduce((sum, col) => sum + (getColumnWidth(col) ?? 0), 0)
    : 0;
  const tableWidth = columnsTotal > containerWidth ? columnsTotal : "100%";

  // 행 병합(rowspan) 계산
  type RowSpanFn = (
    rows: ReturnType<typeof table.getRowModel>["rows"],
    rowIndex: number,
  ) => number;
  const rowSpanMap = React.useMemo(() => {
    const allRows = table.getRowModel().rows;
    const map = new Map<string, { skip: boolean; rowSpan: number }>();
    allRows.forEach((row, rowIdx) => {
      row.getVisibleCells().forEach((cell) => {
        const rowSpanFn = (
          cell.column.columnDef.meta as { rowSpan?: RowSpanFn } | undefined
        )?.rowSpan;
        if (!rowSpanFn) return;
        const key = `${rowIdx}-${cell.column.id}`;
        if (map.has(key)) return;
        const span = rowSpanFn(allRows, rowIdx);
        map.set(key, { skip: false, rowSpan: span });
        for (let i = 1; i < span; i++) {
          map.set(`${rowIdx + i}-${cell.column.id}`, {
            skip: true,
            rowSpan: 0,
          });
        }
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, columns]);

  // 커스텀 리사이즈 핸들러
  const handleResizeStart = React.useCallback(
    (columnId: string, clientX: number) => {
      const columnDef = leafColumns.find((col) => columnKey(col) === columnId);
      const currentWidth =
        columnSizing[columnId] ?? columnDef?.size ?? columnDef?.minSize ?? 0;

      setResizingColumnId(columnId);
      resizeStartX.current = clientX;
      resizeStartWidth.current = currentWidth;
    },
    [columnSizing, leafColumns],
  );

  const handleResizeMove = React.useCallback(
    (clientX: number) => {
      if (!resizingColumnId) return;

      const delta = clientX - resizeStartX.current;
      const newWidth = resizeStartWidth.current + delta;

      // 최소 너비 제한
      const columnDef = leafColumns.find(
        (col) => columnKey(col) === resizingColumnId,
      );
      const minAllowedSize = columnDef?.minSize ?? columnDef?.size ?? 0;
      const constrainedWidth = Math.max(newWidth, minAllowedSize);

      // 이후 컨테이너 리사이즈 시 자동 분배가 이 폭을 덮어쓰지 않게 표시한다
      manuallyResizedRef.current.add(resizingColumnId);
      setColumnSizing((prev) => ({
        ...prev,
        [resizingColumnId]: constrainedWidth,
      }));
    },
    [resizingColumnId, leafColumns],
  );

  const handleResizeEnd = React.useCallback(() => {
    setResizingColumnId(null);
  }, []);

  // 마우스/터치 이벤트 리스너
  React.useEffect(() => {
    if (!resizingColumnId) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleResizeMove(e.clientX);
    };

    const handleMouseUp = () => {
      handleResizeEnd();
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleResizeMove(e.touches[0].clientX);
      }
    };

    const handleTouchEnd = () => {
      handleResizeEnd();
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [resizingColumnId, handleResizeMove, handleResizeEnd]);

  const totalPages = enablePagination
    ? totalItems !== undefined
      ? Math.ceil(totalItems / pageSize)
      : table.getPageCount()
    : 0;
  const currentPage = enablePagination ? pageIndex + 1 : 1;

  const handlePageChange = (page: number) => {
    table.setPageIndex(page - 1);
  };

  const handlePageSizeChange = (newSize: string) => {
    const size = parseInt(newSize, 10);
    onPaginationChange?.(0, size);
  };

  const displayTotalItems = totalItems ?? data.length;
  // 고정 열은 사용자가 숨길 수 없으니 "열이 모두 숨김" 판단에서 뺀다
  const dispalyTotalColumns = scrollableColumns.length;

  // 스크롤 테이블·고정 열 테이블 둘 다 같은 헤더/바디 렌더러를 쓴다 — 따로 두면 한쪽만
  // 고치고 잊어버리기 쉽다 (예: 행 클릭, rowSpan)
  type HeaderType = ReturnType<
    typeof table.getHeaderGroups
  >[number]["headers"][number];
  type RowType = ReturnType<typeof table.getRowModel>["rows"][number];

  const renderHeaderRow = (
    key: string,
    headers: HeaderType[],
    groupIndex: number,
  ) => (
    <TableRow key={key} className="hover:bg-transparent">
      {headers.map((header, index) => {
        const isLastColumn = index === headers.length - 1;
        // 위 줄의 placeholder 헤더는 아래 줄 헤더를 subHeaders 로 가져서 잎으로 안 보인다
        const isLeaf = header.column.columns.length === 0;
        const rowSpan = isLeaf ? headerGroupCount - groupIndex : 1;
        const canResize = isLeaf && header.column.getCanResize();
        const width = getColumnWidth(header.column);
        const minWidth =
          header.column.columnDef.minSize ?? header.column.columnDef.size;
        const isCentered = hasCenteredContent(header.column.columnDef);
        return (
          <TableHead
            key={header.id}
            stroke={isLastColumn ? "bottom" : "right-bottom"}
            className={cn(isCentered && "px-0!")}
            colSpan={header.colSpan > 1 ? header.colSpan : undefined}
            rowSpan={rowSpan > 1 ? rowSpan : undefined}
            style={{ position: "relative", width, minWidth }}
            onClick={
              header.column.getCanSort()
                ? () => toggleOrder?.(header.column.id)
                : undefined
            }
          >
            <div className={cn("flex", isCentered ? "justify-center" : "justify-between")}>
              {flexRender(header.column.columnDef.header, header.getContext())}
              {header.column.getCanSort() && (
                <UpDown
                  size={24}
                  color="labels-assistive"
                  className="shrink-0"
                  order={
                    header.column.id === orderBy ? orderDirection : undefined
                  }
                />
              )}
            </div>
            {enableColumnResizing && canResize && (
              <div
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  const originalSize =
                    header.column.columnDef.size ??
                    header.column.columnDef.minSize;
                  if (originalSize) {
                    setColumnSizing((prev) => ({
                      ...prev,
                      [header.column.id]: originalSize,
                    }));
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleResizeStart(header.column.id, e.clientX);
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  if (e.touches.length > 0) {
                    handleResizeStart(header.column.id, e.touches[0].clientX);
                  }
                }}
                className={cn(
                  "absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none",
                  "hover:bg-primary-normal/50",
                  resizingColumnId === header.column.id && "bg-primary-normal",
                )}
                style={{ transform: "translateX(-50%)" }}
              />
            )}
          </TableHead>
        );
      })}
    </TableRow>
  );

  const renderSkeletonRow = (
    rowIndex: number,
    skeletonColumns: ReturnType<typeof table.getAllLeafColumns>,
    keyPrefix: string,
  ) => (
    <TableRow key={`${keyPrefix}-row-${rowIndex}`}>
      {skeletonColumns.map((column, colIndex) => {
        const isLastColumn = colIndex === skeletonColumns.length - 1;
        const width = getColumnWidth(column);
        const minWidth = column.columnDef.minSize ?? column.columnDef.size;
        return (
          <TableCell
            key={`${keyPrefix}-cell-${rowIndex}-${colIndex}`}
            stroke={isLastColumn ? "bottom" : "right-bottom"}
            className={cn(hasCenteredContent(column.columnDef) && "px-0!")}
            style={{ width, minWidth }}
          >
            <Skeleton className="h-5 w-full" />
          </TableCell>
        );
      })}
    </TableRow>
  );

  const renderBodyRow = (
    row: RowType,
    rowIdx: number,
    isIncluded: (cell: ReturnType<RowType["getVisibleCells"]>[number]) => boolean,
  ) => (
    <TableRow
      key={row.id}
      data-state={
        (row.getIsSelected() || row.id === activeRowId) && "selected"
      }
      {...(onRowClick && {
        [ROW_CLICKABLE_ATTR]: "",
        className: "cursor-pointer",
        onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
          if (isRowClick(e)) onRowClick(row.original);
        },
      })}
    >
      {row
        .getVisibleCells()
        .filter(isIncluded)
        .map((cell, index, cells) => {
          const spanKey = `${rowIdx}-${cell.column.id}`;
          const spanInfo = rowSpanMap.get(spanKey);
          if (spanInfo?.skip) return null;

          const isLastColumn = index === cells.length - 1;
          const width = getColumnWidth(cell.column);
          const minWidth =
            cell.column.columnDef.minSize ?? cell.column.columnDef.size;
          const rowSpan = spanInfo?.rowSpan ?? 1;
          return (
            <TableCell
              key={cell.id}
              stroke={isLastColumn ? "bottom" : "right-bottom"}
              className={cn(
                hasCenteredContent(cell.column.columnDef) && "px-0!",
              )}
              style={{ width, minWidth }}
              rowSpan={rowSpan > 1 ? rowSpan : undefined}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          );
        })}
    </TableRow>
  );

  return (
    <div className={cn("flex flex-col gap-4", fillHeight && "h-full")}>
      {showToolbar && (
        <div className="flex items-center justify-between shrink-0">
          <p className="typo-body-2-medium text-labels-alternative">
            {toolbarLeftLabel ??
              `조회 항목 ${formatCount(displayTotalItems)}건`}
          </p>
          <div className="flex items-center gap-3">
            {bulkActionsSlot}
            {enableColumnSettings && columnSettingsSlot}
            <Select
              value={pageSize.toString()}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger
                // NOTI !: shadow-xs와 충졸되어서 shadow-[inset_0_0_0_1px_var(--lines-strong)]!로 직접 설정해줌...
                className="typo-label-1-semibold text-labels-primary whitespace-nowrap w-fit bg-static-white h-9! rounded-md px-3 py-2 shadow-[inset_0_0_0_1px_var(--lines-strong)]! gap-1 border-none transition-none focus-visible:ring-0 **:data-[slot=select-icon]:hidden"
              >
                {pageSize}개씩 보기
                <ChevronDown size={16} color="labels-primary" />
              </SelectTrigger>
              <SelectContent
                align="end"
                className="w-40 min-w-35 px-2 py-3"
                itemGap="gap-1"
              >
                {pageSizeOptions.map((size) => (
                  <SelectItem
                    key={size}
                    value={size.toString()}
                    className="px-2 py-1.5 typo-label-1-semibold"
                    showCheckIcon
                  >
                    {size}개씩 보기
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      {!isLoading && displayTotalItems <= 0 ? (
        <div
          className={cn(
            "flex flex-col gap-4 items-center text-center",
            fillHeight ? "flex-1 min-h-0 justify-center" : "p-58",
          )}
        >
          <div className="p-3.5 bg-fills-neutral w-fit h-fit rounded-4xl">
            <FileIcon color="lines-strong" />
          </div>
          <span className="typo-body-1-bold text-labels-alternative">
            표시할 데이터가 없어요
            <br />
            <span className="typo-body-2-medium text-labels-alternative">
              데이터가 생성되면 이곳에 보여져요.
            </span>
          </span>
        </div>
      ) : (
        <div className="flex rounded-lg overflow-hidden inset-border-solid-normal shrink-0">
          <div
            ref={attachContainer}
            className="overflow-x-auto min-w-0 flex-1"
          >
            <Table
              ref={tableRef}
              className="table-fixed"
              style={{ width: tableWidth ?? "100%" }}
            >
              <colgroup>
                {scrollableColumns.map((column) => (
                  <col
                    key={column.id}
                    style={{ width: getColumnWidth(column) }}
                  />
                ))}
              </colgroup>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup, groupIndex) =>
                  renderHeaderRow(
                    headerGroup.id,
                    // 그룹 컬럼이 있으면 헤더가 여러 줄이 된다. TanStack 은 잎 헤더를 맨 아래
                    // 줄에 정렬하고 위 줄에는 placeholder 를 두는데, rowSpan 은 위 줄에서
                    // 선언해야 하므로 컬럼 깊이가 이 줄과 같은 헤더만 그린다
                    headerGroup.headers.filter(
                      (header) =>
                        header.column.depth === groupIndex &&
                        !isStickyRightColumn(header.column.columnDef),
                    ),
                    groupIndex,
                  ),
                )}
              </TableHeader>
              <TableBody>
                {isLoading
                  ? Array.from({ length: skeletonRowCount }, (_, rowIndex) =>
                      renderSkeletonRow(rowIndex, scrollableColumns, "skeleton"),
                    )
                  : table
                      .getRowModel()
                      .rows.map((row, rowIdx) =>
                        renderBodyRow(row, rowIdx, (cell) =>
                          !isStickyRightColumn(cell.column.columnDef),
                        ),
                      )}
              </TableBody>
            </Table>
          </div>

          {/* 데이터 열이 전부 숨겨졌으면 액션 버튼만 남기지 않고 같이 감춘다 */}
          {stickyColumns.length > 0 && scrollableColumns.length > 0 && (
            <div
              className="shrink-0 rounded-r-lg overflow-hidden sticky-column-shadow bg-bg-primary"
              style={{ width: stickyTotalWidth }}
            >
              <Table className="table-fixed" style={{ width: stickyTotalWidth }}>
                <colgroup>
                  {stickyColumns.map((column) => (
                    <col
                      key={column.id}
                      style={{ width: getColumnWidth(column) }}
                    />
                  ))}
                </colgroup>
                <TableHeader>
                  {renderHeaderRow(
                    "sticky-header",
                    table
                      .getHeaderGroups()
                      .at(-1)!
                      .headers.filter((header) =>
                        isStickyRightColumn(header.column.columnDef),
                      ),
                    headerGroupCount - 1,
                  )}
                </TableHeader>
                <TableBody>
                  {isLoading
                    ? Array.from({ length: skeletonRowCount }, (_, rowIndex) =>
                        renderSkeletonRow(
                          rowIndex,
                          stickyColumns,
                          "sticky-skeleton",
                        ),
                      )
                    : table
                        .getRowModel()
                        .rows.map((row, rowIdx) =>
                          renderBodyRow(row, rowIdx, (cell) =>
                            isStickyRightColumn(cell.column.columnDef),
                          ),
                        )}
                </TableBody>
              </Table>
              {hasHorizontalScroll && <div className="h-4 bg-black/[0.02]" />}
            </div>
          )}
        </div>
      )}

      {!isLoading && dispalyTotalColumns <= 0 && displayTotalItems > 0 && (
        <div className="flex flex-col gap-4 items-center text-center p-58">
          <div className="p-3.5 bg-fills-neutral w-fit h-fit rounded-4xl">
            <FileIcon color="lines-strong" />
          </div>
          <span className="typo-body-1-bold text-labels-alternative">
            열이 모두 숨김 상태예요 <br />
            <span className="typo-body-2-medium text-labels-alternative">
              열 표시/순서 편집 기능에서 열이 보이게 체크해 주세요.
            </span>
          </span>
        </div>
      )}

      {enablePagination && displayTotalItems > 0 && (
        <div className="flex justify-center mt-9 mb-30 shrink-0">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
}

// `{name}↗︎` 처럼 배열로 들어오는 경우도 텍스트로 본다
function isTextOnlyChildren(children: React.ReactNode): boolean {
  return React.Children.toArray(children).every(
    (child) => typeof child === "string" || typeof child === "number",
  );
}

/**
 * children 컨테이너 클래스.
 * 엘리먼트에 truncate 를 걸면 nowrap 때문에 중첩 박스가 줄지 못해 말줄임 없이 잘린다 —
 * 폭만 제약하고 말줄임은 자식에게 맡긴다.
 */
function contentTextClassName(children: React.ReactNode) {
  return isTextOnlyChildren(children)
    ? "truncate min-w-0"
    : "flex min-w-0 items-center *:min-w-0";
}

// 공통 테이블 콘텐츠 Props
interface TableContentProps {
  /** 문자열/숫자면 말줄임이 자동 적용된다. 엘리먼트면 자식이 `truncate` 를 직접 가져야 한다 */
  children?: React.ReactNode;
  checkbox?: {
    checked: boolean | "indeterminate";
    onCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
  };
  leftIcon?: React.ReactNode;
  leftBadge?: React.ReactNode;
  rightIcon?: React.ReactNode;
  rightBadge?: React.ReactNode;
  link?: {
    onClick?: () => void;
    href?: string;
  };
  button?: {
    label: string;
    onClick: () => void;
    variant?: "solid" | "outlined";
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
  };
  toggle?: {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
  };
  textField?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  sortable?: {
    direction: "asc" | "desc" | false;
    onSort: () => void;
  };
  variant?: "title" | "body";
  className?: string;
}

// 테이블 헤더 콘텐츠 컴포넌트
type TableHeaderContentProps = TableContentProps;

function TableHeaderContent({
  children,
  checkbox,
  leftIcon,
  leftBadge,
  rightIcon,
  rightBadge,
  link,
  button,
  toggle,
  textField,
  variant = "title",
  className,
}: TableHeaderContentProps) {
  const textClassName = cn(
    contentTextClassName(children),
    variant === "title" ? "typo-body-2-semibold" : "typo-body-2-medium",
  );

  // props 개수 카운트 (children 제외)
  const propsCount = [
    checkbox,
    leftIcon,
    leftBadge,
    rightIcon,
    rightBadge,
    button,
    toggle,
    textField,
  ].filter(Boolean).length;

  const renderText = () => {
    if (!children) return null;

    if (link) {
      if (link.href) {
        return (
          <a
            href={link.href}
            className={cn(
              textClassName,
              "text-primary-normal hover:text-primary-strong underline",
            )}
          >
            {children}
          </a>
        );
      }
      return (
        <button
          type="button"
          className={cn(
            textClassName,
            "text-primary-normal hover:text-primary-strong underline text-left",
          )}
          onClick={link.onClick}
        >
          {children}
        </button>
      );
    }

    return <span className={textClassName}>{children}</span>;
  };

  const content = (
    <div
      className={cn(
        "flex items-center min-w-0 w-full gap-1",
        propsCount >= 3 && "gap-2",
        propsCount === 2 && "justify-between",
        className,
      )}
    >
      {checkbox && (
        <Checkbox
          checked={checkbox.checked}
          onCheckedChange={checkbox.onCheckedChange}
          disabled={checkbox.disabled}
          aria-label="모두 선택"
        />
      )}
      {leftIcon && <span className="shrink-0">{leftIcon}</span>}
      {leftBadge && <span className="shrink-0">{leftBadge}</span>}
      {renderText()}
      {textField && (
        <input
          type="text"
          value={textField.value}
          onChange={(e) => textField.onChange(e.target.value)}
          placeholder={textField.placeholder}
          className="flex-1 min-w-0 px-2 py-1 bg-white rounded-md typo-body-2-medium text-labels-primary placeholder:text-labels-assistive inset-border-solid-normal"
        />
      )}

      {rightIcon && <span className="shrink-0">{rightIcon}</span>}
      {button && (
        <button
          type="button"
          onClick={button.onClick}
          className={cn(
            "shrink-0 -my-1 flex items-center gap-1 px-2 py-1.75 rounded-sm typo-caption-1-semibold",
            button.variant === "outlined"
              ? "bg-white inset-border-solid-normal text-labels-primary"
              : "bg-fills-neutral inset-border-double text-labels-primary",
          )}
        >
          {button.leftIcon}
          <span>{button.label}</span>
          {button.rightIcon}
        </button>
      )}
      {toggle && (
        <Switch
          checked={toggle.checked}
          onCheckedChange={toggle.onCheckedChange}
          className="shrink-0"
        />
      )}
      {rightBadge && <span className="flex shrink-0">{rightBadge}</span>}
    </div>
  );

  return content;
}

// 테이블 셀 콘텐츠 컴포넌트
type TableCellContentProps = Omit<TableContentProps, "sortable">;

function TableCellContent({
  children,
  checkbox,
  leftIcon,
  leftBadge,
  rightIcon,
  rightBadge,
  link,
  button,
  toggle,
  textField,
  variant = "body",
  className,
}: TableCellContentProps) {
  const textClassName = cn(
    contentTextClassName(children),
    variant === "title" ? "typo-body-2-semibold" : "typo-body-2-medium",
  );

  // props 개수 카운트 (children 제외)
  const propsCount = [
    checkbox,
    leftIcon,
    leftBadge,
    rightIcon,
    rightBadge,
    button,
    toggle,
    textField,
  ].filter(Boolean).length;

  const renderText = () => {
    if (!children) return null;

    if (link) {
      if (link.href) {
        return (
          <a
            href={link.href}
            className={cn(
              textClassName,
              "text-labels-primary underline cursor-pointer",
            )}
          >
            {children}
          </a>
        );
      }
      return (
        <button
          type="button"
          className={cn(
            textClassName,
            "text-labels-primary underline text-left cursor-pointer",
          )}
          onClick={link.onClick}
        >
          {children}
        </button>
      );
    }

    return <span className={textClassName}>{children}</span>;
  };

  return (
    <div
      className={cn(
        "flex items-center min-w-0 w-full gap-1",
        propsCount >= 3 ? "gap-2" : "justify-between",
        className,
      )}
    >
      {checkbox && (
        <Checkbox
          checked={checkbox.checked}
          onCheckedChange={checkbox.onCheckedChange}
          disabled={checkbox.disabled}
          aria-label="행 선택"
        />
      )}
      {leftIcon && <div className="flex">{leftIcon}</div>}
      {leftBadge && <div className="flex">{leftBadge}</div>}
      {renderText()}
      {textField && (
        <input
          type="text"
          value={textField.value}
          onChange={(e) => textField.onChange(e.target.value)}
          placeholder={textField.placeholder}
          className="flex-1 min-w-0 px-2 py-1 bg-white rounded-md typo-body-2-medium text-labels-primary placeholder:text-labels-assistive inset-border-solid-normal"
        />
      )}
      {rightIcon && <div className="flex">{rightIcon}</div>}
      {button && (
        <button
          type="button"
          onClick={button.onClick}
          className={cn(
            "shrink-0 -my-1 flex items-center gap-1 px-2 py-1.75 rounded-sm typo-caption-1-semibold",
            button.variant === "outlined"
              ? "bg-white inset-border-solid-normal text-labels-primary"
              : "bg-fills-neutral inset-border-double text-labels-primary",
          )}
        >
          {button.leftIcon}
          <span>{button.label}</span>
          {button.rightIcon}
        </button>
      )}

      {toggle && (
        <Switch
          checked={toggle.checked}
          onCheckedChange={toggle.onCheckedChange}
        />
      )}
      {rightBadge && <span className="flex shrink-0">{rightBadge}</span>}
    </div>
  );
}

export { DataTable, TableHeaderContent, TableCellContent, ROW_CLICKABLE_ATTR };
